import { useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';

import { ROUTES } from '../../constants';
import { useAlerts, useModal } from '../../hooks';
import { useRBAC } from '../../hooks/useRBAC';
import { NAV_ITEMS, NAV_LABELS } from '../../pages/landingPage/constants';
import { ASSET_PATHS } from '../../utils/assetPath';
import { useAuth } from '../auth/AuthContext';
import SessionTimeoutWarning from '../auth/SessionTimeoutWarning';
import ActiveOutOfOfficeBanner from '../ActiveOutOfOfficeBanner';
import { ApplicationBannerRegion } from '../ApplicationBanner';
import AuthenticatedWelcomeRow from '../AuthenticatedWelcomeRow';
import { DismissibleAlert } from '../DismissibleAlert';
import Footer from '../Footer/Footer';
import { FooterLinkProps } from '../Footer/FooterLink';
import Header from '../Header/Header';
import { Modal } from '../Modal';
import TopNavigationBar from '../TopNavigationBar/TopNavigationBar';
import './Layout.css';
import { shouldShowAuthenticatedWelcome } from './authenticatedWelcomeVisibility';

const ERROR_CODES: Record<string, string> = {
  DEFAULT_ERROR: 'An error occurred',
  ENTITY_MANAGER_URL_NOT_FOUND: 'Entity manager URL not found',
  ERROR_VALIDATING_INVITATION_TOKEN: 'Error validating invitation token',
  IDM_USERINFO_FAILED: 'Your login could not be completed due to an unexpected error. Please try again later.',
  INVALID_TOKEN_FORMAT: 'Invalid invitation token format',
  INVITATION_EXPIRED:
    'Your invitation link has expired. Please contact your COR (Contract Officer Representative) to request a new invitation.',
  INVITATION_TOKEN_VALIDATION_FAILED: 'Invitation token validation failed',
};

const ERROR_CODE_ALERT_TITLES: Partial<Record<string, string>> = {
  IDM_USERINFO_FAILED: 'Login failed',
  INVITATION_EXPIRED: 'Invitation expired',
};

const ownershipSection = (
  <div className='cms-footer__detail-wrap'>
    <img
      alt='HHS.gov logo'
      aria-hidden='true'
      className='cms-footer__hhs'
      src={ASSET_PATHS.HHS_LOGO}
    />
    <p className='cms-footer_legal-text'>
      A federal government website managed and paid for by the U.S. Centers for Medicare & Medicaid Services. 7500
      Security Boulevard, Baltimore, MD 21244
    </p>
  </div>
);

interface LayoutProps {
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

function Layout({ 'data-testid': dataTestId }: LayoutProps) {
  const { isOpen, content, heading, size, className: modalClassName, closeModal } = useModal();
  const { alerts, dismissAlert, errorAlert } = useAlerts();
  const { isAuthenticated, isInitialized, isLoggingOut } = useAuth();
  const { capabilities } = useRBAC();
  const location = useLocation();
  const alertsContainerRef = useRef<HTMLDivElement | null>(null);
  const isLandingPage = location.pathname === ROUTES.HOME;
  const keepAuthenticated = isAuthenticated || isLoggingOut;
  const isPublicLandingPage = isLandingPage && isInitialized && !keepAuthenticated;
  const showAuthenticatedWelcome =
    isInitialized && isAuthenticated && shouldShowAuthenticatedWelcome(location.pathname);
  const mainClassName = isPublicLandingPage
    ? 'ds-l-container layout-main--public-landing'
    : 'ds-l-container ds-u-margin-y--6';
  const footerClassName = isPublicLandingPage
    ? 'cms-footer--landing-public'
    : keepAuthenticated && isLandingPage
    ? 'cms-footer--landing-auth'
    : undefined;
  const baseTestId = dataTestId ?? 'layout';
  const footerLinks = useMemo<FooterLinkProps[]>(() => {
    const links: FooterLinkProps[] = NAV_ITEMS.slice(0, 3).map((item) => ({
      href: item.path,
      label: item.label,
      openInNewTab: item.isExternal,
    }));
    const novaHref = capabilities.canAccessApplication
      ? ROUTES.NOVA_APPLICATION
      : capabilities.canAccessApprovals
      ? ROUTES.APPROVALS
      : null;

    if (novaHref) {
      links.push({ href: novaHref, label: NAV_LABELS.NOVA });
    }
    if (capabilities.canAccessContracts) {
      links.push(...NAV_ITEMS.slice(3).map((item) => ({ href: item.path, label: item.label })));
    }

    return links;
  }, [capabilities.canAccessApplication, capabilities.canAccessApprovals, capabilities.canAccessContracts]);

  const [searchParams] = useSearchParams();
  const handledErrorCodeRef = useRef<string | null>(null);

  useEffect(() => {
    const errorCode = searchParams.get('error_code');

    if (!errorCode || handledErrorCodeRef.current === errorCode) {
      return;
    }

    handledErrorCodeRef.current = errorCode;
    const errorMessage = ERROR_CODES[errorCode] || ERROR_CODES.DEFAULT_ERROR;
    const isStandaloneMessage = errorCode in ERROR_CODE_ALERT_TITLES;
    errorAlert({
      message: isStandaloneMessage ? errorMessage : `${errorMessage} Please try again later.`,
      title: ERROR_CODE_ALERT_TITLES[errorCode] ?? 'An error has occurred.',
    });
  }, [errorAlert, searchParams]);

  useEffect(() => {
    if (alerts.length === 0) {
      return;
    }

    if (typeof alertsContainerRef.current?.scrollIntoView === 'function') {
      alertsContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [alerts.length]);

  return (
    <div
      className={isPublicLandingPage ? 'layout layout--public-landing' : 'layout'}
      data-testid={baseTestId}>
      <Header data-testid={`${baseTestId}-header`} />
      {isInitialized && keepAuthenticated && <TopNavigationBar data-testid={`${baseTestId}-top-nav`} />}
      {showAuthenticatedWelcome && (
        <div
          className='layout__authenticated-welcome'
          data-testid={`${baseTestId}-authenticated-welcome`}>
          <div className='ds-l-container'>
            <AuthenticatedWelcomeRow data-testid={`${baseTestId}-authenticated-welcome-row`} />
          </div>
        </div>
      )}
      <main
        className={mainClassName}
        data-testid={`${baseTestId}-main`}
        id='main'
        role='main'
        tabIndex={-1}>
        {isInitialized && isAuthenticated && (
          <ApplicationBannerRegion data-testid={`${baseTestId}-application-banners`}>
            <ActiveOutOfOfficeBanner />
          </ApplicationBannerRegion>
        )}
        {alerts.length > 0 && (
          <div
            ref={alertsContainerRef}
            data-testid={`${baseTestId}-alerts`}>
            {alerts.map((alert) => (
              <DismissibleAlert
                key={alert.id}
                className='ds-u-margin-bottom--2'
                data-testid={`${baseTestId}-alert-${alert.id}`}
                heading={alert.title}
                variation={alert.variation}
                onDismiss={() => dismissAlert(alert.id)}>
                {alert.message}
              </DismissibleAlert>
            ))}
          </div>
        )}
        <Outlet />
      </main>
      <Modal
        className={modalClassName}
        data-testid={`${baseTestId}-modal`}
        heading={heading}
        isOpen={isOpen}
        size={size}
        onClose={closeModal}>
        {content}
      </Modal>
      <Footer
        className={footerClassName}
        data-testid={`${baseTestId}-footer`}
        links={keepAuthenticated ? footerLinks : []}
        ownershipSection={ownershipSection}
        subsidiaryAgencyLogo={isPublicLandingPage ? ASSET_PATHS.CMS_LOGO : undefined}
        subsidiaryAgencyLogoAlt={isPublicLandingPage ? 'CMS.gov' : undefined}
      />
      {isInitialized && isAuthenticated && <SessionTimeoutWarning />}
    </div>
  );
}

export default Layout;
